<template>
  <vx-card no-shadow class="overflow-show">
    <div id="user-edit-tab-account">
      <!-- Col Header -->
      <div class="flex items-end mb-3" v-if="showHeader">
        <feather-icon icon="UserIcon" class="mr-2" svgClasses="w-5 h-5" />
        <span class="leading-none font-medium">{{ $t('users.account') }}</span>
      </div>

      <vs-alert v-if="!data.province" color="warning" class="mb-base text-big">
        {{ $t('messages.select_province_to_continue') }}
      </vs-alert>

      <!-- Avatar Row -->
      <div class="vx-row">
        <upload-file
          class="vx-col w-full"
          title=""
          v-model="data_local.avatar"
          @upload-complete="upload_complete"
        >
          <div class="vx-col w-full">
            <div class="flex items-start flex-col sm:flex-row">
              <vs-avatar :src="avatarSrc" size="80px" class="mr-4 shadow-md cursor-default" />
              <div>
                <p class="text-lg font-medium mb-2 mt-4 sm:mt-0">
                  {{ data_local.details.firstName }} {{ data_local.details.lastName }}
                </p>
                <vs-button v-if="data_local.avatar" type="border" @click="remove_avatar" color="danger"
                  >{{ $t('forms.remove') }} {{ $t('users.avatar') }}</vs-button
                >
              </div>
            </div>
          </div>
        </upload-file>
      </div>
      <!-- Content Row -->
      <div class="vx-row">
        <div class="vx-col md:w-2/3 w-full">
          <vs-input
            class="w-full mt-4"
            :label="$t('users.username')"
            v-model="data_local.username"
            v-validate="'required|phone:mobile'"
            :data-vv-as="$t('users.username')"
            name="username"
            disabled
          />
          <span class="text-danger text-sm" v-show="errors.has('username')">{{
            errors.first('username')
          }}</span>

          <vs-input
            class="w-full mt-4"
            :label="$t('users.first_name')"
            v-model="data_local.details.firstName"
            v-validate="'required|letters'"
            :data-vv-as="$t('users.first_name')"
            name="firstName"
          />
          <span class="text-danger text-sm" v-show="errors.has('firstName')">{{
            errors.first('firstName')
          }}</span>

          <vs-input
            class="w-full mt-4"
            :label="$t('users.last_name')"
            v-model="data_local.details.lastName"
            v-validate="'required|letters'"
            :data-vv-as="$t('users.last_name')"
            name="lastName"
          />
          <span class="text-danger text-sm" v-show="errors.has('lastName')">{{
            errors.first('lastName')
          }}</span>

          <div class="mt-4">
            <label class="vs-input--label">{{ $t('users.addresses.province') }}</label>
            <v-select
              v-model="data_local.province"
              :clearable="false"
              :options="provinceOptions"
              :reduce="option => option.value"
              name="province"
              v-validate="'required'"
              :data-vv-as="$t('users.addresses.province')"
              :dir="$vs.rtl ? 'rtl' : 'ltr'"
            />
            <span class="text-danger text-sm" v-show="errors.has('province')">{{
              errors.first('province')
            }}</span>
          </div>

          <vs-input
            class="w-full mt-4"
            :label="$t('users.email')"
            v-model="data_local.email"
            v-validate="'required|email'"
            data-vv-validate-on="blur"
            :data-vv-as="$t('users.email')"
            type="email"
            name="email"
          />
          <span class="text-danger text-sm" v-show="errors.has('email')">{{ errors.first('email') }}</span>
        </div>
      </div>

      <!-- Save & Reset Button -->
      <div class="vx-row">
        <div class="vx-col w-full">
          <div class="mt-8 flex flex-wrap items-center justify-end">
            <vs-button class="ml-auto mt-2" @click="save_changes" :disabled="!validateForm">{{
              $t('forms.update')
            }}</vs-button>
            <vs-button class="ml-4 mt-2" type="border" color="warning" @click="reset_data">{{
              $t('forms.reset')
            }}</vs-button>
          </div>
        </div>
      </div>
    </div>
  </vx-card>
</template>

<script>
import UploadFile from '@/views/components/forms/UploadFile'

export default {
  components: { UploadFile },
  props: {
    data: {
      type: Object,
      required: true
    },
    showHeader: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      data_local: JSON.parse(JSON.stringify(this.data)),
      avatarSrc: null,
      colorLoading: 'primary'
    }
  },
  computed: {
    validateForm() {
      return !this.errors.any()
    },
    provinceOptions() {
      return [
        { value: 'استان کرمان', label: 'کرمان' },
        { value: 'استان خراسان رضوی', label: 'خراسان رضوی' },
        { value: 'استان تهران', label: 'تهران' },
        { value: 'استان خراسان شمالی', label: 'خراسان شمالی' },
        { value: 'استان ایلام', label: 'ایلام' },
        { value: 'استان لرستان', label: 'لرستان' },
        { value: 'استان مرکزی', label: 'مرکزی' },
        { value: 'استان چهارمحال و بختیاری', label: 'چهارمحال و بختیاری' },
        { value: 'استان کرمانشاه', label: 'کرمانشاه' },
        { value: 'استان همدان', label: 'همدان' },
        { value: 'استان قزوین', label: 'قزوین' },
        { value: 'استان گیلان', label: 'گیلان' },
        { value: 'استان زنجان', label: 'زنجان' },
        { value: 'استان سمنان', label: 'سمنان' },
        { value: 'استان اصفهان', label: 'اصفهان' },
        { value: 'استان کهگیلویه و بویر احمد', label: 'کهگیلویه و بویر احمد' },
        { value: 'استان کردستان', label: 'کردستان' },
        { value: 'استان آذربایجان غربی', label: 'آذربایجان غربی' },
        { value: 'استان فارس', label: 'فارس' },
        { value: 'استان بوشهر', label: 'بوشهر' },
        { value: 'استان اردبیل', label: 'اردبیل' },
        { value: 'استان مازندران', label: 'مازندران' },
        { value: 'استان گلستان', label: 'گلستان' },
        { value: 'استان خراسان جنوبی', label: 'خراسان جنوبی' },
        { value: 'استان سیستان و بلوچستان', label: 'سیستان و بلوچستان' },
        { value: 'استان قم', label: 'قم' },
        { value: 'استان البرز', label: 'البرز' },
        { value: 'استان آذربایجان شرقی', label: 'آذربایجان شرقی' },
        { value: 'استان یزد', label: 'یزد' },
        { value: 'استان هرمزگان', label: 'هرمزگان' },
        { value: 'استان خوزستان', label: 'خوزستان' }
      ]
    }
  },
  methods: {
    upload_complete() {
      this.$store.dispatch('auth/editProfile', { avatar: this.data_local.avatar }).then(() => {
        this.$store.commit('UPDATE_USER_INFO', { avatar: this.data_local.avatar })
        this.get_avatar()
        // eslint-disable-next-line no-self-assign
        window.location = window.location
      })
    },
    save_changes() {
      if (!this.validateForm) return
      // eslint-disable-next-line no-unused-vars
      const { id, ...userData } = this.data_local
      if (userData.details) delete userData.details['_id']
      if (userData.location) delete userData.location['_id']
      if (userData.password) delete userData['password']
      this.$store
        .dispatch('auth/editProfile', userData)
        // eslint-disable-next-line no-unused-vars
        .then(res => {
          this.$store.commit('UPDATE_USER_INFO', {
            displayName: `${res.data.details.firstName} ${res.data.details.lastName}`,
            ...res.data
          })
          this.$notifySuccess(this.$t('forms.successfully_edited'))

          if (this.$route.query.to) this.$router.push(this.$route.query.to)
        })
    },
    reset_data() {
      this.data_local = JSON.parse(JSON.stringify(this.data))
    },
    remove_avatar() {
      this.data_local.avatar = ''
      this.avatar = null
      this.$store.dispatch('auth/editProfile', { avatar: '' }).then(() => {
        this.avatarSrc = null
        window.location = '/pages/login'
        // eslint-disable-next-line no-self-assign
        window.location = window.location
      })
    },
    get_avatar() {
      if (!this.data_local.avatar) return

      this.$store.dispatch('file/downloadFile', this.data_local.avatar).then(response => {
        this.avatarSrc = response
        this.$set(this.$store.state.AppActiveUser, 'avatarSrc', response)
        this.$store.commit('UPDATE_USER_INFO', { avatarSrc: response })
      })
    }
  },
  created() {
    this.get_avatar()
  }
}
</script>
<style>
.vs__dropdown-menu {
  z-index: 9000;
}
.con-vs-tabs,
.con-vs-tabs .con-slot-tabs {
  overflow: visible !important;
}
</style>
